import { push } from 'connected-react-router';
import { all, call, put, race, select, spawn, take } from 'redux-saga/effects';
import { serviceUnavailable } from '../../modules/app/actions';
import { ConflictError, ConnectionError, NotFoundError, ServiceUnavailableError } from '../../modules/app/errors';
import { close, open } from '../../modules/modal/actions';
import { ACTIVATE, ENTER_SHOP_ID } from '../../routes/paths';
import * as form from '../FormMessage/actions';
import * as notify from '../layout/Notifications/actions';
import {
  ACTIVATE_VIEW_ENTER,
  CARD_NUMBER_CONFIRM,
  CARD_NUMBER_REJECT,
  CARD_NUMBER_SUBMIT,
  PHONE_NUMBER_CONFIRM,
  PHONE_NUMBER_REJECT,
  REGISTER_CARD_ATTEMPT,
  RESEND_ATTEMPT,
  RESEND_CONFIRM,
  RESEND_REJECT,
  setStep,
  SHOP_CONFIRM,
  SHOP_REJECT,
  shopDetailsFailure,
  shopDetailsSuccess,
  SUBMIT_SHOP_ID
} from './actions';
import { STEP_PHONE_NUMBER, STEP_SUCCESS } from './constants';
import { REASON_PHONE_USED, RegisterBadRequestError, RegisterConflictError } from './errors';
import { checkCardNumber, getShopDetails, registerCard, resendConfirmationMessage } from './rest';
import { getCardNumber, getPhoneNumber } from './selectors';
import { getShopId, storeShopId } from './storage';

function* watchSubmitLBO() {
  while (true) {
    const { shopId } = yield take(SUBMIT_SHOP_ID);
    try {
      const shopDetails = yield call(getShopDetails, shopId);
      yield put(shopDetailsSuccess(shopDetails));
      yield put(open({ componentName: 'ShopIdConfirm' }));
      const { confirm } = yield race({
        confirm: take(SHOP_CONFIRM),
        reject: take(SHOP_REJECT)
      });
      yield put(close());
      if (confirm) {
        yield call(storeShopId, shopId);
        yield put(push(ACTIVATE));
      } else {
        yield call(storeShopId, '');
      }
    } catch (e) {
      yield put(shopDetailsFailure());
      if (e instanceof ConnectionError) {
        yield put(notify.add('Connection error.', notify.ERROR));
      } else if (e instanceof NotFoundError) {
        yield put(open({ componentName: 'ShopIdNotFound' }));
      } else if (e instanceof ServiceUnavailableError) {
        yield put(serviceUnavailable());
      } else {
        throw e;
      }
    }
  }
}

function* watchActivateViewEnter() {
  while (yield take(ACTIVATE_VIEW_ENTER)) {
    const shopId = yield call(getShopId);
    if (!shopId) {
      yield put(push(ENTER_SHOP_ID));
    }
  }
}

function* watchCardNumberSubmit() {
  while (true) {
    const { cardNumber } = yield take(CARD_NUMBER_SUBMIT);
    try {
      yield put(open({ componentName: 'RequestInProgress' }));
      yield call(checkCardNumber, cardNumber);
      yield put(close());
      yield put(open({ componentName: 'ConfirmCardNumber', componentProps: { cardNumber } }));
      const { confirm } = yield race({
        confirm: take(CARD_NUMBER_CONFIRM),
        reject: take(CARD_NUMBER_REJECT)
      });
      yield put(close());
      if (confirm) {
        yield put(setStep(STEP_PHONE_NUMBER));
      }
    } catch (error) {
      yield put(close());
      if (error instanceof ConnectionError) {
        yield put(notify.add('Connection error.', notify.ERROR));
      } else if (error instanceof NotFoundError) {
        yield put(open({ componentName: 'CardNumberRegistered', componentProps: { error } }));
      } else if (error instanceof ConflictError) {
        yield put(open({ componentName: 'CardNumberRegistered', componentProps: { error } }));
      } else if (error instanceof ServiceUnavailableError) {
        yield put(serviceUnavailable());
      } else {
        throw error;
      }
    }
  }
}

function* watchRegisterCardAttempt() {
  while (true) {
    try {
      const { phoneNumber, marketingConsent } = yield take(REGISTER_CARD_ATTEMPT);
      const shopId = yield call(getShopId);
      const cardNumber = yield select(getCardNumber);
      const consents = {
        marketing: marketingConsent
      };

      yield put(open({ componentName: 'ConfirmPhoneNumber' }));
      const { confirm } = yield race({
        confirm: take(PHONE_NUMBER_CONFIRM),
        reject: take(PHONE_NUMBER_REJECT)
      });
      yield put(close());
      if (confirm) {
        yield put(open({ componentName: 'RequestInProgress' }));
        yield call(registerCard, shopId, cardNumber, phoneNumber, consents);
        yield put(close());
        yield put(form.clearMessage());
        yield put(setStep(STEP_SUCCESS));
      }
    } catch (e) {
      yield put(close());
      if (e instanceof ConnectionError) {
        yield put(notify.add('Connection error.', notify.ERROR));
      } else if (e instanceof RegisterConflictError && Array.isArray(e.details.reason)
        && e.details.reason.includes(REASON_PHONE_USED)) {
        yield put(open({ componentName: 'PhoneAlreadyRegistered' }));
      } else if (e instanceof RegisterBadRequestError || e instanceof RegisterConflictError) {
        yield put(notify.add('Can\'t register provided phone or card.', notify.ERROR));
      } else if (e instanceof ServiceUnavailableError) {
        yield put(serviceUnavailable());
      } else {
        throw e;
      }
    }
  }
}

function* watchResendConfirmationAttempt() {
  while (yield take(RESEND_ATTEMPT)) {
    try {
      const phoneNumber = yield select(getPhoneNumber);
      yield put(open({ componentName: 'ResendConfirmationMessage' }));
      const { confirm } = yield race({
        confirm: take(RESEND_CONFIRM),
        reject: take(RESEND_REJECT)
      });
      yield put(close());
      if (confirm) {
        yield put(open({ componentName: 'RequestInProgress' }));
        yield call(resendConfirmationMessage, phoneNumber);
        yield put(close());
        yield put(form.setMessage(form.SUCCESS, 'Confirmation message has been sent again.'));
      }
    } catch (e) {
      yield put(close());
      if (e instanceof ConnectionError) {
        yield put(notify.add('Connection error.', notify.ERROR));
      } else if (e instanceof NotFoundError) {
        yield put(form.setMessage(form.ERROR, 'Cannot send confirmation message.'));
      } else if (e instanceof ServiceUnavailableError) {
        yield put(serviceUnavailable());
      } else {
        throw e;
      }
    }
  }
}

export default function* root() {
  yield all([
    spawn(watchSubmitLBO),
    spawn(watchActivateViewEnter),
    spawn(watchCardNumberSubmit),
    spawn(watchRegisterCardAttempt),
    spawn(watchResendConfirmationAttempt)
  ]);
}
